import type { ReactNode } from "react";

import { classes } from "./classes.js";

/**
 * `P0-UIK-001`. 4.6.3's primary is `indigo-600` filled, `indigo-700` on hover.
 *
 * **It cannot be `disabled` here.** `disabled` is deliberately absent from these
 * props: `P0-UIK-002`'s `GuardedButton` owns that, because invariant **I19**
 * says a disabled control must always carry its reason, and a `Button` with a
 * bare `disabled` prop is the hole that rule exists to close. A caller who
 * wants a disabled action reaches for the component that demands the reason.
 *
 * **`inactive` is the CLIENT app's case, and I19's own scope line is why it is
 * not the same thing.** I19 reads: *"UI test on every guarded button in the
 * console, the dealer space and the bank app: when disabled, a non-empty reason
 * string is rendered next to it."* Three surfaces, and the client app is not
 * among them , deliberately, because WF-CLI-01 states the opposite rule for a
 * buyer in terms: the phone field's control is inactive until eight digits are
 * typed *"(no message, the control is simply inactive)"*. A person half-way
 * through typing their own number has not made a mistake, and telling them so is
 * worse than saying nothing.
 *
 * So the two words mean two things and the type keeps them apart: `disabled`
 * belongs to `GuardedButton` and always carries a reason; `inactive` is a form
 * that is not finished being filled in. **A console or bank screen reaching for
 * `inactive` is reaching for the wrong one**, and the reason it is named rather
 * than aliased is so that reach is visible in review.
 */
/**
 * `F-1`: `ink` and `danger` are the bank's second and third weights beside the one indigo primary (D2's « Valider » /
 * « Refuser »): ink is a decision made quietly, danger a refusal. Sizes `sm` and `lg` are the prototype's 34 and 48 px.
 * The bank's skin reads `data-variant` and `data-size`; the other apps draw the plain classes below.
 */
export type ButtonVariant = "primary" | "secondary" | "ghost" | "ink" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps {
  readonly children: ReactNode;
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly type?: "button" | "submit";
  readonly onClick?: () => void;
  readonly className?: string;
  /** WF-CLI-01's "the control is simply inactive". CLIENT surfaces only. */
  readonly inactive?: boolean;
  /**
   * `F-0c`: the same as `inactive`, under the name a caller reaches for.
   *
   * **Native `disabled` is for TRANSIENT unavailability only** (an action in flight, a form submitting): it clears by
   * itself and there is nothing for a banker to learn from it. **Any control that is unavailable for a reason the
   * banker should know is a `GuardedButton`, with the reason beside it** (D-1019-2; DX-D-006: no exemption). A
   * `disabled` button leaves the tab order and says nothing, which is exactly what I19 forbids for a standing refusal.
   */
  readonly disabled?: boolean;
  /** `F-0c`: a toggle's state (the review's toggle acts). Absent, the button is not a toggle and says nothing. */
  readonly "aria-pressed"?: boolean;
  /**
   * `KIT-DISCLOSURE`: **`aria-pressed` is for a selection whose state the label does not already say** (a toggle that
   * stays « Filtrer » whether it is on or off). **`aria-expanded` is for a disclosure**: a control that shows or hides
   * a region and says so by its own state (« Afficher la synthèse » / « Masquer la synthèse », a bell and its panel,
   * « Reporter » and its form). A screen reader announces « développé » or « réduit » for it, and « enfoncé » would
   * be the wrong word. Absent, the button says neither. No `aria-controls`: the region is not always in the DOM.
   */
  readonly "aria-expanded"?: boolean;
  /**
   * `KIT-BUTTON-ARIA`: the accessible name and description a caller gives. **A hyphenated JSX attribute is never checked for excess**, so
   * `aria-label` on a `<Button>` compiled while this component dropped it, and the bank's « Pièce reçue : {texte} » was never spoken.
   * `aria-controls` stays out on purpose (`KIT-DISCLOSURE`: the region is not always in the DOM).
   */
  readonly "aria-label"?: string;
  readonly "aria-labelledby"?: string;
  readonly "aria-describedby"?: string;
  /**
   * `09-09` requires a `data-testid` for every element an end-to-end test
   * selects, and **this component silently dropped every one it was given.**
   *
   * Found by the walk of 6 September's own spec: `R68`'s assertion could not
   * find « Ajouter un véhicule » at all, because the id was written on a
   * `<Button>` and never reached the DOM. Every control this round put an id
   * on , the add panel's two, the profile's three, the fiche's three , was
   * unselectable, and a test that looked for one would have read `null` and
   * been told the control was absent when it was merely anonymous.
   */
  readonly "data-testid"?: string;
  /**
   * `P1-DLW-054` (designer R, batch 12, the architect's ruling on row primaries): a row's primary says that the DEALER's gesture is awaited on it. The mark is a hook for the checks and the walk, drawn
   * only when given; the kit never infers it.
   */
  readonly "data-awaits"?: "dealer";
}

const VARIANTS: Readonly<Record<ButtonVariant, string>> = {
  primary: "bg-indigo-600 text-white hover:bg-indigo-700",
  secondary: "bg-white text-gray-900 border border-gray-200 hover:bg-gray-50",
  ghost: "text-gray-500 hover:text-gray-900",
  ink: "bg-gray-900 text-white",
  danger: "bg-white text-rose-700 border border-rose-200 hover:bg-rose-50",
};

export function Button({
  children,
  variant = "primary",
  size = "md",
  type = "button",
  onClick,
  className,
  inactive = false,
  disabled = false,
  "aria-pressed": pressed,
  "aria-expanded": expanded,
  "aria-label": label,
  "aria-labelledby": labelledBy,
  "aria-describedby": describedBy,
  "data-testid": testId,
  "data-awaits": awaits,
}: ButtonProps): React.JSX.Element {
  return (
    <button
      type={type === "submit" ? "submit" : "button"}
      onClick={onClick}
      {...(testId === undefined ? {} : { "data-testid": testId })}
      {...(awaits === undefined ? {} : { "data-awaits": awaits })}
      disabled={inactive || disabled}
      aria-pressed={pressed}
      aria-expanded={expanded}
      aria-label={label}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      data-kit="button"
      data-variant={variant}
      data-size={size}
      className={classes(
        "rounded-xl px-4 py-2 text-sm font-medium",
        VARIANTS[variant],
        // Visibly inactive, and not merely unclickable: a control that looks
        // pressable and does nothing is worse than one that looks unavailable.
        inactive || disabled ? "cursor-not-allowed opacity-50" : "",
        className,
      )}
    >
      {children}
    </button>
  );
}
